import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { getAppSettings, updateAppSetting } from "../../utils/backend";

const SETTING_LABELS = {
  request_more_quotation_enabled: {
    title: 'Allow "Request More Quotation"',
    description:
      'When enabled, the "I wish to request more quotation" option automatically appears on the Get Insurance Quotation page (ANSA app and web) whenever fewer than 2 insurer quotes are returned. When disabled, the option is never shown.',
  },
};

const Settings = () => {
  const [settings, setSettings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await getAppSettings();
      setSettings(response.data || []);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error(error.message || "Failed to load settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = async (setting, checked) => {
    const value = checked ? "1" : "0";
    const previousValue = setting.value;
    const label = SETTING_LABELS[setting.key]?.title || setting.key;

    setSavingKey(setting.key);
    setSettings((prev) =>
      prev.map((s) => (s.key === setting.key ? { ...s, value } : s))
    );

    try {
      await updateAppSetting({ key: setting.key, value });
      toast.success(`${label} ${checked ? "enabled" : "disabled"}`);
    } catch (error) {
      setSettings((prev) =>
        prev.map((s) =>
          s.key === setting.key ? { ...s, value: previousValue } : s
        )
      );
      toast.error(error.message || "Failed to update setting");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="card border-light">
      <div className="card-body">
        <h5 className="card-title mb-4">App Settings</h5>
        {isLoading ? (
          <>
            <Skeleton height={30} width="100%" />
            <Skeleton height={30} width="100%" style={{ marginTop: 10 }} />
          </>
        ) : settings.length === 0 ? (
          <p className="text-muted mb-0">No settings available.</p>
        ) : (
          settings.map((setting) => {
            const label = SETTING_LABELS[setting.key] || {
              title: setting.key,
              description: setting.description,
            };
            return (
              <div
                key={setting.key}
                className="d-flex align-items-start justify-content-between py-3 border-bottom"
              >
                <div className="me-4">
                  <div className="fw-semibold">{label.title}</div>
                  <div className="text-muted" style={{ fontSize: "14px" }}>
                    {label.description}
                  </div>
                </div>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id={`setting-${setting.key}`}
                    checked={setting.value === "1"}
                    disabled={savingKey === setting.key}
                    onChange={(e) => handleToggle(setting, e.target.checked)}
                    style={{ cursor: "pointer", transform: "scale(1.4)" }}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Settings;
